import { ReactNode } from 'react';
import { Check } from '@untitledui/icons';
import { cx } from '@/utils/cx';

export interface StepConfig {
    title: string;
    description?: string;
}

interface StepperProps {
    currentStep: number;
    steps: StepConfig[];
    completedSteps: number[];
    validSteps: number[];
    onStepChange?: (step: number) => void;
    children: ReactNode;
    className?: string;
    allowStepNavigation?: boolean;
}

export const Stepper = ({
    currentStep,
    steps,
    completedSteps = [],
    validSteps = [],
    onStepChange,
    children,
    className,
    allowStepNavigation = false
}: StepperProps) => {
    const handleStepClick = (stepNumber: number) => {
        if (allowStepNavigation && onStepChange) {
            // Allow navigation only to completed steps or the next step
            const canNavigate = completedSteps.includes(stepNumber) ||
                stepNumber === currentStep ||
                (stepNumber === currentStep + 1 && validSteps.includes(currentStep));

            if (canNavigate) {
                onStepChange(stepNumber);
            }
        }
    };

    return (
        <div className={cx("space-y-8", className)}>
            {/* Stepper Navigation */}
            <div className="bg-primary rounded-xl p-6 shadow-xs ring-1 ring-secondary">
                <div className="flex items-center justify-center">
                    {steps.map((step, index) => {
                        const stepNumber = index + 1;
                        const isActive = currentStep === stepNumber;
                        const isCompleted = completedSteps.includes(stepNumber);
                        const isClickable = allowStepNavigation && (
                            isCompleted ||
                            stepNumber === currentStep ||
                            (stepNumber === currentStep + 1 && validSteps.includes(currentStep))
                        );

                        return (
                            <div key={stepNumber} className="flex items-center">
                                {/* Step Circle and Info */}
                                <div
                                    className={cx(
                                        "flex flex-col items-center text-center",
                                        isClickable && "cursor-pointer hover:opacity-80 transition-opacity"
                                    )}
                                    onClick={() => isClickable && handleStepClick(stepNumber)}
                                >
                                    {/* Step Circle */}
                                    <div
                                        className={cx(
                                            "flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200 mb-3",
                                            isCompleted && "bg-brand-solid border-brand-solid dark:text-white",
                                            isActive && !isCompleted && "border-brand-solid bg-brand-solid dark:text-white",
                                            !isActive && !isCompleted && "border-secondary bg-primary text-tertiary"
                                        )}
                                    >
                                        {isCompleted ? (
                                            <Check className="w-5 h-5" />
                                        ) : (
                                            <span className="text-sm font-semibold">{stepNumber}</span>
                                        )}
                                    </div>

                                    {/* Step Content */}
                                    <div className="max-w-[140px]">
                                        <h3
                                            className={cx(
                                                "text-sm font-semibold transition-colors duration-200",
                                                isActive ? "text-primary" : "text-secondary"
                                            )}
                                        >
                                            {step.title}
                                        </h3>
                                    </div>
                                </div>

                                {/* Divider Line */}
                                {index < steps.length - 1 && (
                                    <div className="mx-8">
                                        <div
                                            className={cx(
                                                "h-px w-16 transition-colors duration-200",
                                                isCompleted ? "bg-brand-solid" : "bg-secondary"
                                            )}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Step Content */}
            <div>
                {children}
            </div>
        </div>
    );
};
